import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Accordion,
  Paper,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AgGridReact } from 'ag-grid-react';
import NetsuiteLoadingDialog from '../../UserDialog/NetsuiteLoadingDialog';
import axios from 'axios';
import JSZip from 'jszip';
import { toast } from 'react-toastify';
import { useNetSuiteProductionRfq } from '../../../config/netsuite';

const PoNetsuitePreviewModal = ({ open, onClose, netsuitePreviewData, onSubmissionComplete, rfqId }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionResults, setSubmissionResults] = useState([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [poAttachmentsMap, setPoAttachmentsMap] = useState({});
  const [isAttachmentsLoaded, setIsAttachmentsLoaded] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  if (!Array.isArray(netsuitePreviewData)) return null;

  const columnDefs = [
    { headerName: 'Line Item #', field: 'lineItemNumber' },
    { headerName: 'Description', field: 'description' },
    { headerName: 'Quantity', field: 'quantity' },
    { headerName: 'UOM', valueGetter: (p) => p.data?.custcoluom?.refName || '—' },
    { headerName: 'Hide Qty/Rate', valueGetter: (p) => p.data?.custcol_bpc_hide_qty_rate },
    { headerName: 'Unit Price', field: 'rate', valueFormatter: currencyFormatter },
    { headerName: 'Subtotal', field: 'custcol_bpc_subtotal', valueFormatter: currencyFormatter },
    { headerName: 'Tax Amount', field: 'custcol_bpc_tax_amount', valueFormatter: currencyFormatter },
    { headerName: 'Total', field: 'amount', valueFormatter: currencyFormatter },
    { headerName: 'Lead Time', valueGetter: (p) => p.data?.lead_time || '—' },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 100,
    sortable: true,
    filter: true,
    resizable: true,
  };

  useEffect(() => {
    setIsAttachmentsLoaded(false);
    if (!rfqId || !netsuitePreviewData.length) return;

    const fetchAttachments = async () => {
      try {
        const [internalRes, ...vendorResList] = await Promise.all([
          axios.get(`/api/RfqHeader/fetchAttachments/${rfqId}`),
          ...netsuitePreviewData.map((group) =>
            axios.get(`/api/RfqHeader/fetchAttachments/${rfqId}?vendorId=${group.nsVendorId}`)
          ),
        ]);

        const newMap = {};
        newMap['internal'] = internalRes.data || [];

        netsuitePreviewData.forEach((group, idx) => {
          newMap[group.nsVendorId] = vendorResList[idx].data || [];
        });

        setPoAttachmentsMap(newMap);
        setIsAttachmentsLoaded(true);
      } catch (err) {
        console.error('Failed to fetch PO attachments:', err);
        toast.error('Attachment load failed');
      }
    };

    fetchAttachments();
  }, [rfqId, netsuitePreviewData]);

  const handleDownloadGroupAttachments = async (vendorName, projectName) => {
    try {
      const zip = new JSZip();
      const internalFiles = poAttachmentsMap['internal'] || [];

      if (internalFiles.length) {
        const internalFolder = zip.folder('Internal_Files');
        for (const file of internalFiles) {
          const blob = await getBlobFile(file.blobName);
          const fileName = file.fileName.split('/').pop();
          internalFolder.file(fileName, blob);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${projectName}_${vendorName}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      toast.error('Download failed');
    }
  };

  const totalAwarded = netsuitePreviewData
    .flatMap((p) => p.translatedPayloadForDisplay?.item?.items || [])
    .reduce((sum, i) => sum + (i.amount || 0), 0);

  const handleSubmitToNetSuite = () => setConfirmOpen(true);

  const submitAllPoPayloads = async () => {
    if (submitting) return; // ✅ Prevent duplicate in-progress submissions
    setSubmitting(true);
    setSubmissionResults([]);

    let allSucceeded = true;

    for (const group of netsuitePreviewData) {
      try {
        const vendorName = group.nsVendorName;
        const projectName = group.projectName;

        setLoadingMessage(`Submitting PO for ${projectName} - ${vendorName}...`);

        const payload = group.nsPayload;
        const displayItems = group.translatedPayloadForDisplay?.item?.items || [];
        // POST PO to NetSuite
        setLoadingMessage(`Creating PO for ${projectName} - ${vendorName}...`);
        const postRes = await axios.post(
          `/api/NetSuite/PurchaseOrder?useProduction=${useNetSuiteProductionRfq}`,
          payload
        );
        const poResultData = postRes.data;
        const poId = poResultData.recordId;
        const tranId = poResultData.transactionId;

        // PATCH special instructions
        setLoadingMessage(`Patching instructions for PO ${poId}...`);
        const instructions = `Vendor Instructions: ${group.vendorInstructions || 'N/A'}\n\nDelivery Location: ${group.deliveryLocation || 'N/A'}`;
        const vendorInstructionsPatch = {
          custbody_bpc_special_instructions: instructions,
        };
        await axios.patch(
          `/api/NetSuite/PurchaseOrder/${poId}?useProduction=${useNetSuiteProductionRfq}`,
          vendorInstructionsPatch
        );

        // PATCH each line item's BidSheet with poId
        setLoadingMessage(`Updating bid sheet lines for PO ${poId}...`);
        for (const item of displayItems) {
          const res = await axios.get(`/api/RfqVendorBidSheet/?poLineItemId=${item.poLineItemId}`);
          const bidItemId = res.data[0]?.id;
          if (bidItemId) {
            const patchPayload = [{ op: 'replace', path: '/netsuitePoId', value: poId }];
            await axios.patch(`/api/RfqVendorBidSheet/${bidItemId}`, patchPayload);
          }
        }

        // Upload attachments
        setLoadingMessage(`Uploading attachments for PO ${poId}...`);
        const filesToUpload = [...(poAttachmentsMap['internal'] || []), ...(poAttachmentsMap[group.nsVendorId] || [])];
        for (const file of filesToUpload) {
          try {
            const blobFile = file.blobName ? await getBlobFile(file.blobName) : null;
            const fileToUpload = blobFile ? convertBlobToFile(blobFile, file.blobName) : file;
            const formData = new FormData();
            formData.append('file', fileToUpload);

            await axios.post(
              `/api/NetSuite/PurchaseOrder/AttachFile?purchaseOrderId=${poId}&useProduction=${useNetSuiteProductionRfq}`,
              formData
            );
          } catch (err) {
            console.warn(`File upload failed for: ${file.blobName}`, err);
          }
        }

        setSubmissionResults((prev) => [
          ...prev,
          <>
            <strong>PO Submitted Successfully.</strong>
            <br />
            NetSuite ID: {poId}
            <br />
            Transaction ID: {tranId}
          </>,
        ]);
      } catch (err) {
        allSucceeded = false;
        console.error(err);

        const errorMessage = err?.response?.data?.toString().toLowerCase() || '';

        if (err.response?.status === 403) {
          toast.error('Elevated permissions required for this action.');
        } else if (errorMessage.includes('subsidiary')) {
          toast.error('Submission Failed: Vendor Not Approved For This Subsidiary');
          setSubmissionResults((prev) => [...prev, `Submission Failed: Vendor Not Approved For This Subsidiary.`]);
        } else {
          setSubmissionResults((prev) => [
            ...prev,
            `Failed to submit PO for Project ${group.projectName}. Check console.`,
          ]);
        }
      }
    }

    setSubmitting(false);
    setConfirmOpen(false);

    if (allSucceeded) setHasSubmitted(true);
    if (allSucceeded && onSubmissionComplete) onSubmissionComplete();
  };

  // Helper to fetch blob content
  const getBlobFile = async (blobItemPath) => {
    const encodedPath = encodeURIComponent(blobItemPath);
    const res = await axios.get(`/api/RfqHeader/GetBlobFileContent?blobItemPath=${encodedPath}`, {
      responseType: 'blob',
    });
    return res.data;
  };

  // Helper to convert blob to File
  const convertBlobToFile = (blob, blobItemPath) => {
    const fileName = blobItemPath.split('/').pop();
    return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xxl" fullWidth PaperProps={{ sx: { minHeight: '90vh' } }}>
      <DialogTitle>
        <strong>Netsuite Purchase Order Confirmation</strong>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={10}>
            {netsuitePreviewData.map((group, index) => (
              <Accordion key={index}>
                <AccordionSummary
                  expandIcon={
                    <Box sx={{ p: 1 }}>
                      <ExpandMoreIcon fontSize="large" sx={{ color: 'text.primary' }} />
                    </Box>
                  }
                  sx={{ p: 0 }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      width: '100%',
                      backgroundColor: '#f9f9f9',
                      borderLeft: '4px solid #1976d2',
                    }}
                  >
                    <Grid container spacing={2}>
                      {/* Left Column */}
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          Project Name
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                          {group.projectName || '—'}
                        </Typography>

                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          Vendor
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          {group.nsVendorName || '—'}
                        </Typography>

                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          PO Description
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          {group.translatedPayloadForDisplay?.memo || '—'}
                        </Typography>

                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          Delivery Location
                        </Typography>
                        <Typography variant="subtitle1">{group.deliveryLocation || '—'}</Typography>
                      </Grid>

                      {/* Right Column */}
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          NetSuite Subsidiary Name
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          {group.nsSubsidiaryName || '—'}
                        </Typography>

                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          Work Scope
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          {group.translatedPayloadForDisplay?.custbody_bpc_po_work_scope?.name || '—'}
                        </Typography>

                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          Delivery Date
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          {formatDate(group.translatedPayloadForDisplay?.custbody4) || '—'}
                        </Typography>

                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                          Line Item Count
                        </Typography>
                        <Typography variant="subtitle1">
                          {group.translatedPayloadForDisplay?.item?.items?.length || 0} Item(s)
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0 }}>
                  <Box>
                    <div style={{ width: '100%' }}>
                      <AgGridReact
                        rowData={(group.translatedPayloadForDisplay?.item?.items || [])
                          .slice()
                          .sort((a, b) => (a.lineItemNumber ?? 0) - (b.lineItemNumber ?? 0))}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        domLayout="autoHeight"
                      />
                    </div>
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Button
                        variant="contained"
                        onClick={() => handleDownloadGroupAttachments(group.nsVendorName, group.projectName)}
                      >
                        Download Attachments
                      </Button>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Purchase Order Total (with tax): $
                        {(
                          group.translatedPayloadForDisplay?.item?.items?.reduce(
                            (sum, item) => sum + (item.amount || 0),
                            0
                          ) || 0
                        ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" fontWeight="bold" align="right">
              Combined Purchase Order Total(s) (with tax): $
              {totalAwarded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSubmitToNetSuite}
                disabled={!isAttachmentsLoaded || hasSubmitted || submitting}
              >
                Submit NetSuite PO(s)
              </Button>
              <Button variant="contained" color="secondary" fullWidth onClick={onClose}>
                Close
              </Button>
            </Box>
            {submissionResults.map((msg, idx) => (
              <Typography key={idx} variant="body2" component="div" sx={{ mb: 1 }}>
                {msg}
              </Typography>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions />
      <NetsuiteLoadingDialog
        open={confirmOpen}
        message="Are you sure you want to submit all POs to NetSuite? This action cannot be undone."
        loadingMessage={loadingMessage}
        loading={submitting}
        onConfirm={submitAllPoPayloads}
        onCancelClick={() => setConfirmOpen(false)}
        confirmLabel="Yes"
        cancelLabel="Cancel"
      />
    </Dialog>
  );
};

function currencyFormatter(params) {
  const val = parseFloat(params.value);
  return isNaN(val) ? '' : `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  try {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
  } catch {
    return 'N/A';
  }
}

export default PoNetsuitePreviewModal;
